import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PhoneCall, PhoneOff, ChevronLeft, ChevronRight } from "lucide-react";
import { API, useAuth } from "@/App";

const fmt = (iso) => { try { return `${new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })} ET`; } catch { return iso; } };
const Pill = ({ children, bg = "#f1f5f9", color = "#334155", testid }) => (
  <span data-testid={testid} style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 600, background: bg, color }}>{children}</span>
);
const Row = ({ label, children }) => (
  <div style={{ display: "flex", gap: 10, fontSize: 12, lineHeight: 1.45 }}>
    <span style={{ width: 96, flexShrink: 0, color: "#94a3b8", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 2 }}>{label}</span>
    <span style={{ flex: 1, color: "#0f172a", whiteSpace: "pre-wrap" }}>{children}</span>
  </div>
);

function OutcomeCard({ o }) {
  const connected = o.contact_status === "Connected";
  const when = o.followup_date ? ` · ${o.followup_date}${o.followup_time ? ` ${o.followup_time}` : ""}` : "";
  const extra = o.info_products?.length ? ` — ${o.info_products.join(", ")}` : o.trial_product ? ` — ${o.trial_product}` : o.demo_products?.length ? ` — ${o.demo_products.join(", ")}` : "";
  return (
    <div data-testid={`ehi-outcome-${o.id}`} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderLeft: `3px solid ${connected ? "#1D9E75" : "#BA7517"}`, borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {connected ? <PhoneCall style={{ width: 13, height: 13, color: "#1D9E75" }} /> : <PhoneOff style={{ width: 13, height: 13, color: "#BA7517" }} />}
        <Pill testid={`ehi-outcome-status-${o.id}`} bg={connected ? "#DAFBE8" : "#FFF3CD"} color={connected ? "#127152" : "#856404"}>{o.contact_status}</Pill>
        <span style={{ fontSize: 12, color: "#334155" }}>{connected ? o.contact_person : o.not_connected_reason}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748b" }}>{fmt(o.created_at)} · {o.rep_name}</span>
      </div>
      {connected && <Row label="Pitched">{o.products_pitched.join(", ")}</Row>}
      {connected && <Row label="Interest">{o.interest_level}{o.not_interested_reason ? ` — ${o.not_interested_reason}` : ""}</Row>}
      <Row label="Next action"><span data-testid={`ehi-outcome-next-${o.id}`}>{o.next_action}{when}{extra}</span></Row>
      {o.task_id && <Row label="Task"><span data-testid={`ehi-outcome-task-${o.id}`} style={{ color: "#175cd3" }}>Task created for {o.rep_name} — see the client's Tasks tab</span></Row>}
      {o.notes && <Row label="Notes">{o.notes}</Row>}
    </div>
  );
}

const PAGE = 5;

/** Log outcome history for one EHI client, newest first, 5 per page. */
export default function EhiOutcomeHistory({ clientId, refreshKey }) {
  const { token } = useAuth();
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ outcomes: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    axios.get(`${API}/ehi-clients/${clientId}/outcomes`, { params: { limit: PAGE, skip: page * PAGE }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, page, token]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  return (
    <div data-testid="ehi-outcome-history" style={{ background: "#fafafa", border: "0.5px solid #eee", borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>Log outcome history</span>
        <span data-testid="ehi-outcome-total" style={{ fontSize: 10, fontWeight: 700, background: "#e5e7eb", color: "#555", borderRadius: 999, padding: "1px 7px" }}>{data.total}</span>
      </div>
      {loading && <div className="skeleton" style={{ height: 56, borderRadius: 10 }} />}
      {!loading && data.total === 0 && <div data-testid="ehi-outcome-empty" style={{ fontSize: 12, color: "#94a3b8", padding: "10px 0" }}>No call outcomes logged yet.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.outcomes.map((o) => <OutcomeCard key={o.id} o={o} />)}
      </div>
      {data.total > PAGE && (
        <div data-testid="ehi-outcome-pagination" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, fontSize: 11, color: "#64748b" }}>
          <button data-testid="ehi-outcome-prev" className="btn" disabled={page === 0} onClick={() => setPage((x) => x - 1)} style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3, opacity: page === 0 ? 0.5 : 1 }}>
            <ChevronLeft style={{ width: 12, height: 12 }} /> Prev
          </button>
          <span data-testid="ehi-outcome-page">Page {page + 1} of {pages}</span>
          <button data-testid="ehi-outcome-next" className="btn" disabled={page >= pages - 1} onClick={() => setPage((x) => x + 1)} style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 3, opacity: page >= pages - 1 ? 0.5 : 1 }}>
            Next <ChevronRight style={{ width: 12, height: 12 }} />
          </button>
        </div>
      )}
    </div>
  );
}
